import { CookieManager } from '@cm-frontend/shared/cookie-manager';
import { willRedirect } from '@cm-frontend/utils/redirect.utils';
import { merge as _merge, get as _get } from 'lodash-es';

export default (axios, storeMerge = {}) => {
  const store = {
    namespaced: true,
    state: {
      account: {
        uuid: null,
        email: null,
        username: null,
        type: null,
        emailVerified: null,
        phoneVerified: null,
        paymentType: null,
        firstName: null,
        lastName: null,
        phone: null,
        roles: null,
        ipApiCountryName: null,
        ipApiRegion: null,
        settings: { language: null }
      },
      address: {
        addressId: null,
        country: null,
        city: null,
        addressDetails: null
      },
      auth: { accessToken: null, tokenType: null },
      distributorCompany: {
        distributorCompanyId: null,
        code: null,
        name: null,
        active: false,
        imageUrl: null,
        eventUrl: null,
        ssoIntegrated: false,
        options: {}
      },
      schoolImageURL: null,
      schoolName: null,
      edLevel: null
    },
    getters: {
      isDistributorSsoIntegrated(state, getters) {
        return getters.hasDistributor && state.distributorCompany.ssoIntegrated;
      },
      hasDistributor(state) {
        return !!(
          state.distributorCompany.active &&
          state.distributorCompany.distributorCompanyId
        );
      },
      distributorName(state) {
        return state.distributorCompany.name;
      },
      distributorImage(state) {
        return state.distributorCompany.imageUrl;
      },
      distributorOptions(state) {
        return state.distributorCompany.options;
      },
      distributorOptionsCSS(state) {
        return (state.distributorCompany.options || {}).css || {};
      },
      schoolImageURL(state) {
        if (
          state.schoolImageURL &&
          typeof state.schoolImageURL === 'string' &&
          state.schoolImageURL.length
        ) {
          return state.schoolImageURL;
        }
        return null;
      },
      schoolName(state) {
        return state.schoolName;
      },
      isEdLevelPrimarySchool(state) {
        return state.edLevel === 'PRIMARY_SCHOOL';
      },
      userLanguage(state) {
        if (state.account.settings && state.account.settings.language) {
          return state.account.settings.language;
        }
        return 'en';
      },
      api(state, getters, rootState, rootGetters) {
        return axios.create({
          baseURL: rootGetters['CodementumUrl/panelApi'],
          timeout: 60000,
          headers: {
            Authorization: getters.authorizationHeader,
            'Accept-Language': getters.userLanguage
          }
        });
      },
      apiRequest(state, getters, rootState, rootGetters) {
        return async ({
          method,
          path,
          body = {},
          opt = {},
          onSuccess,
          onFailure
        }) => {
          let req;
          if (method === 'post' || method === 'put' || method === 'patch') {
            req = getters.api[method](path, body, opt);
          } else {
            req = getters.api[method](path, { ...opt, ...body });
          }
          try {
            const {
              data: { success, message, data }
            } = await req;
            if (success) {
              if (onSuccess) onSuccess(data);
              return Promise.resolve(data);
            } else {
              if (onFailure) onFailure();
              return Promise.reject(message);
            }
          } catch ({ response }) {
            const { errorCode, message } = response.data;
            const mode = rootGetters['CodementumUrl/buildMode'];
            if (errorCode === 1200) {
              CookieManager.remove(CookieManager.AUTH_COOKIE_NAME);
            }
            if (
              errorCode === 1200 &&
              ['production', 'staging'].includes(mode)
            ) {
              const panelLoginUrl = new URL(
                rootGetters['CodementumUrl/panel'] + 'login'
              );
              const { redirect, url } = willRedirect(panelLoginUrl);
              if (redirect) window.location = url;
            }
            if (onFailure) onFailure(errorCode, message);
            return Promise.reject(message);
          }
        };
      },
      authorizationHeader(state) {
        return `${state.auth.tokenType} ${state.auth.accessToken}`;
      },
      fullName(state, getters) {
        if (getters.isAuthenticated) {
          const firstName = state.account.firstName || '';
          const lastName = state.account.lastName || '';
          if (firstName === '' && lastName === '')
            return state.account.username;
          return firstName + ' ' + lastName;
        }
      },
      isAuthenticated(state) {
        return !!state.account.type;
      },
      isStudent(state) {
        return state.account.type === 'STUDENT';
      },
      isParent(state) {
        return state.account.type === 'PARENT';
      },
      isTeacher(state) {
        return state.account.type === 'TEACHER';
      },
      isDeveloper(state) {
        return (
          state.account.type === 'DEVELOPER' || state.account.type === 'ADMIN'
        );
      },
      isAdmin(state) {
        return state.account.type === 'ADMIN';
      },
      isManager(state) {
        return (
          state.account.type === 'MANAGER' || state.account.type === 'ADMIN'
        );
      },
      isPaid(state) {
        return state.account.paymentType === 'PAID';
      },
      isTrial(state) {
        return state.account.paymentType === 'TRIAL';
      },
      isFree(state) {
        return state.account.paymentType === 'FREE';
      },
      isStaff(state) {
        return state.account.paymentType === 'STAFF';
      }
    },
    mutations: {
      fillState(state, { key, values }) {
        if (!key || !values) return;
        const theObject = _get(state, key);
        if (theObject) {
          Object.keys(theObject).forEach((attr) => {
            if (key === 'distributorCompany' && attr === 'options') {
              try {
                theObject[attr] = JSON.parse(values[attr]);
              } catch (_err) {
                theObject[attr] = {};
              }
            } else {
              theObject[attr] = values[attr];
            }
          });
        }
      },
      reset(state) {
        ['account', 'address', 'auth'].forEach((attr) => {
          Object.keys(state[attr]).forEach((key) => {
            state[attr][key] = null;
          });
        });
        state.schoolImageURL = null;
        state.schoolName = null;
      },
      setschoolImageURL(state, value) {
        if (value && value.match(/\.(jpeg|jpg|gif|png)$/) !== null)
          state.schoolImageURL = value;
      },
      setschoolName(state, value) {
        if (value) state.schoolName = value;
      },
      setEdLevel(state, value) {
        if (value) state.edLevel = value;
      }
    },
    actions: {
      fillUserStates(
        { commit },
        { account, address, teacherDetails, studentDetails, distributorCompany }
      ) {
        if (account) {
          commit('fillState', { key: 'account', values: account });
        }
        if (distributorCompany)
          commit('fillState', {
            key: 'distributorCompany',
            values: distributorCompany
          });
        if (address) commit('fillState', { key: 'address', values: address });
        if (studentDetails) {
          commit('setschoolImageURL', studentDetails.schoolImageURL);
          commit('setschoolName', studentDetails.schoolName);
          commit('setEdLevel', studentDetails.edLevel);
        } else if (teacherDetails) {
          commit('setschoolImageURL', teacherDetails.schoolImageURL);
          commit('setschoolName', teacherDetails.schoolName);
        }
      },
      logout({ commit, getters }) {
        return getters
          .apiRequest({ method: 'post', path: 'user/logout' })
          .finally(() => {
            commit('reset');
            CookieManager.remove(CookieManager.AUTH_COOKIE_NAME);
          });
      },
      updateLanguage({ state, getters, dispatch }, language) {
        return getters.apiRequest({
          method: 'patch',
          path: '/user/' + state.account.uuid,
          body: {
            account: {
              settings: JSON.stringify({ ...state.account.settings, language })
            }
          },
          onSuccess: (data) => dispatch('fillUserStates', data)
        });
      },
      fetchState() {},
      postState() {}
    }
  };

  return _merge(store, storeMerge);
};
